/**
 * /darts — the board on a canvas: the dartboard itself, the darts in it, the
 * dart in flight, the aim, the magnifier under a finger, the heat map of a
 * practice session and the short effects of a hit.
 *
 * The board is drawn ONCE per size and theme into an offscreen canvas (wires,
 * sisal, numbers — the expensive part); a frame is that bitmap plus whatever
 * moves. The frame loop runs only while something moves — an idle board costs
 * no CPU (the site's rule: an idle page does not animate, docs/why/infinite-
 * animation.md).
 *
 * Coordinates: everything the outside passes in is NORMALISED (1 = the outer
 * edge of the double ring, +y down) — see geometry.js. Only this file knows
 * pixels.
 */

import { RING, SEGMENTS, SEG_ARC, sectorAngle } from './geometry.js';

// the board fills the square canvas up to 1.2 R: the number ring ends at 1.16 R
const EDGE = 1.2;

export const THEMES = {
    classic: {
        bg0: '#20232b', bg1: '#0b0c10', spot: 'rgba(255,240,215,.10)',
        rim: '#121214', ring: '#0d0d0f', num: '#f4efe4', numFont: 800,
        dark: '#1d1d1f', light: '#ece2c9', red: '#cf2e2b', green: '#0e8a4b',
        wire: 'rgba(214,218,226,.78)', wireHi: 'rgba(255,255,255,.55)', sisal: 0.09, shade: 0.28, led: null,
    },
    contrast: {
        bg0: '#1a1d24', bg1: '#0a0b0e', spot: 'rgba(255,255,255,.06)',
        rim: '#000', ring: '#000', num: '#ffffff', numFont: 900, numScale: 1.12,
        dark: '#111', light: '#fff6dc', red: '#ff3b30', green: '#00b85c',
        wire: 'rgba(255,214,10,.95)', wireHi: null, sisal: 0, shade: 0.1, led: null,
    },
    stage: {
        bg0: '#10203f', bg1: '#04070f', spot: 'rgba(120,170,255,.16)',
        rim: '#0a0a0c', ring: '#08080a', num: '#ffffff', numFont: 800,
        dark: '#18181a', light: '#efe5cd', red: '#d8312e', green: '#0c9150',
        wire: 'rgba(225,229,238,.85)', wireHi: 'rgba(255,255,255,.6)', sisal: 0.08, shade: 0.24,
        led: ['#ffffff', 'rgba(130,180,255,.85)'],
    },
    neon: {
        bg0: '#160b29', bg1: '#050309', spot: 'rgba(255,60,200,.10)',
        rim: '#07060a', ring: '#07060a', num: '#ff6bd5', numFont: 800, numGlow: '#ff2fb9',
        dark: '#0e0d14', light: '#1c1a26', red: '#ff2f7d', green: '#18f0c8',
        wire: 'rgba(120,255,240,.55)', wireHi: null, sisal: 0, shade: 0.05, glow: true, led: null,
    },
};
export const PREMIUM_THEMES = ['stage', 'neon'];

export const PLAYER_COLORS = ['#3b82f6', '#f59e0b', '#22c55e', '#e11d48'];

function seeded(seed) {
    let a = seed >>> 0;
    return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

export function createBoard(canvas, opts = {}) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw Object.assign(new Error('no 2d context'), { code: 'NO_2D' });
    const reduced = !!opts.reducedMotion;
    const st = {
        size: 0, dpr: 1, R: 0, cx: 0, cy: 0, theme: 'classic', cache: null,
        darts: [], flight: null, fx: [], pops: [], shake: 0,
        aim: null, sweep: null, loupe: null, highlight: null, heat: null, hover: null,
        raf: 0, last: 0, onFrame: null, onLand: null, labels: opts.labels || {},
    };

    /* ── size ── */
    function resize(cssSize) {
        const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
        const size = Math.max(120, Math.floor(cssSize));
        if (size === st.size && dpr === st.dpr && st.cache) return false;
        st.size = size; st.dpr = dpr;
        canvas.width = Math.round(size * dpr);
        canvas.height = Math.round(size * dpr);
        canvas.style.width = size + 'px';
        canvas.style.height = size + 'px';
        st.cx = size / 2; st.cy = size / 2;
        st.R = size / 2 / EDGE;
        st.cache = null;
        draw();
        return true;
    }

    function setTheme(name) {
        if (!THEMES[name] || name === st.theme) return;
        st.theme = name;
        st.cache = null;
        draw();
    }

    /* ── the static board, into a cache ── */
    function buildCache() {
        const T = THEMES[st.theme];
        const W = canvas.width;
        const off = document.createElement('canvas');
        off.width = W; off.height = W;
        const c = off.getContext('2d');
        const k = st.dpr, R = st.R * k, cx = W / 2, cy = W / 2;
        // the wall: a spotlight from above on a dark backdrop
        const g = c.createRadialGradient(cx, cy * 0.55, R * 0.2, cx, cy, W * 0.75);
        g.addColorStop(0, T.bg0); g.addColorStop(1, T.bg1);
        c.fillStyle = g; c.fillRect(0, 0, W, W);
        const sp = c.createRadialGradient(cx, cy * 0.7, 0, cx, cy * 0.7, W * 0.62);
        sp.addColorStop(0, T.spot); sp.addColorStop(1, 'rgba(0,0,0,0)');
        c.fillStyle = sp; c.fillRect(0, 0, W, W);
        // LED surround (the «Stage» theme): the ring of light of a TV stage board
        if (T.led) {
            c.save();
            c.shadowColor = T.led[1]; c.shadowBlur = R * 0.12;
            c.strokeStyle = T.led[0]; c.lineWidth = R * 0.022;
            c.beginPath(); c.arc(cx, cy, R * 1.185, 0, Math.PI * 2); c.stroke();
            c.restore();
        }
        // the board's shadow on the wall
        c.save();
        c.shadowColor = 'rgba(0,0,0,.6)'; c.shadowBlur = R * 0.09; c.shadowOffsetY = R * 0.03;
        c.fillStyle = T.rim;
        c.beginPath(); c.arc(cx, cy, R * 1.165, 0, Math.PI * 2); c.fill();
        c.restore();
        // the number ring
        c.fillStyle = T.ring;
        c.beginPath(); c.arc(cx, cy, R * 1.14, 0, Math.PI * 2); c.fill();
        const band = (r0, r1, a0, a1, col) => {
            c.beginPath(); c.arc(cx, cy, r1, a0, a1); c.arc(cx, cy, r0, a1, a0, true); c.closePath();
            c.fillStyle = col; c.fill();
        };
        for (let i = 0; i < 20; i++) {
            const a0 = i * SEG_ARC - SEG_ARC / 2 - Math.PI / 2;
            const a1 = a0 + SEG_ARC;
            const even = i % 2 === 0;
            const single = even ? T.dark : T.light;
            const ring = even ? T.red : T.green;
            band(RING.bullOuter * R, RING.doubleOuter * R, a0, a1, single);
            band(RING.tripleInner * R, RING.tripleOuter * R, a0, a1, ring);
            band(RING.doubleInner * R, RING.doubleOuter * R, a0, a1, ring);
        }
        c.fillStyle = T.green;
        c.beginPath(); c.arc(cx, cy, RING.bullOuter * R, 0, Math.PI * 2); c.fill();
        c.fillStyle = T.red;
        c.beginPath(); c.arc(cx, cy, RING.bullInner * R, 0, Math.PI * 2); c.fill();
        // sisal: fine fibres, radial — the bristle board's grain
        if (T.sisal > 0) {
            const rnd = seeded(7);
            c.save();
            c.beginPath(); c.arc(cx, cy, R, 0, Math.PI * 2); c.clip();
            const n = Math.round(R * R * 0.012);
            for (let i = 0; i < n; i++) {
                const a = rnd() * Math.PI * 2, rr = Math.sqrt(rnd()) * R;
                const x = cx + Math.cos(a) * rr, y = cy + Math.sin(a) * rr;
                const l = (0.5 + rnd()) * k * 1.4;
                c.strokeStyle = rnd() < 0.5 ? `rgba(255,255,255,${T.sisal})` : `rgba(0,0,0,${T.sisal * 1.4})`;
                c.lineWidth = 0.6 * k;
                c.beginPath(); c.moveTo(x, y); c.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l); c.stroke();
            }
            c.restore();
        }
        // shading: the light from above-left makes the board a disc, not a sticker
        if (T.shade > 0) {
            const sh = c.createRadialGradient(cx - R * 0.35, cy - R * 0.45, R * 0.1, cx, cy, R * 1.05);
            sh.addColorStop(0, `rgba(255,255,255,${T.shade * 0.35})`);
            sh.addColorStop(0.55, 'rgba(255,255,255,0)');
            sh.addColorStop(1, `rgba(0,0,0,${T.shade})`);
            c.fillStyle = sh;
            c.beginPath(); c.arc(cx, cy, R * 1.0, 0, Math.PI * 2); c.fill();
        }
        // glow rings (neon)
        if (T.glow) {
            c.save();
            c.globalCompositeOperation = 'lighter';
            for (const [r0, r1] of [[RING.tripleInner, RING.tripleOuter], [RING.doubleInner, RING.doubleOuter]]) {
                c.shadowBlur = R * 0.05;
                for (let i = 0; i < 20; i++) {
                    const a0 = i * SEG_ARC - SEG_ARC / 2 - Math.PI / 2;
                    c.shadowColor = i % 2 === 0 ? T.red : T.green;
                    band(r0 * R, r1 * R, a0, a0 + SEG_ARC, i % 2 === 0 ? 'rgba(255,47,125,.35)' : 'rgba(24,240,200,.30)');
                }
            }
            c.restore();
        }
        // the spider: wires around and across
        const wire = (w, col) => {
            c.strokeStyle = col; c.lineWidth = w;
            for (const rr of [RING.bullInner, RING.bullOuter, RING.tripleInner, RING.tripleOuter, RING.doubleInner, RING.doubleOuter]) {
                c.beginPath(); c.arc(cx, cy, rr * R, 0, Math.PI * 2); c.stroke();
            }
            for (let i = 0; i < 20; i++) {
                const a = i * SEG_ARC + SEG_ARC / 2 - Math.PI / 2;
                c.beginPath();
                c.moveTo(cx + Math.cos(a) * RING.bullOuter * R, cy + Math.sin(a) * RING.bullOuter * R);
                c.lineTo(cx + Math.cos(a) * RING.doubleOuter * R, cy + Math.sin(a) * RING.doubleOuter * R);
                c.stroke();
            }
        };
        wire(Math.max(1, R * 0.0075), T.wire);
        if (T.wireHi) {
            c.save(); c.translate(-0.5 * k, -0.5 * k); wire(Math.max(0.6, R * 0.0028), T.wireHi); c.restore();
        }
        // the numbers: on the ring, upright, a metal ring outside them
        c.strokeStyle = 'rgba(200,205,215,.35)'; c.lineWidth = Math.max(1, R * 0.006);
        c.beginPath(); c.arc(cx, cy, R * 1.0, 0, Math.PI * 2); c.stroke();
        const fs = Math.round(R * 0.105 * (T.numScale || 1));
        c.font = `${T.numFont} ${fs}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
        c.textAlign = 'center'; c.textBaseline = 'middle';
        if (T.numGlow) { c.shadowColor = T.numGlow; c.shadowBlur = R * 0.04; }
        c.fillStyle = T.num;
        for (let i = 0; i < 20; i++) {
            const a = i * SEG_ARC - Math.PI / 2;
            const nr = R * 1.072;
            c.fillText(String(SEGMENTS[i]), cx + Math.cos(a) * nr, cy + Math.sin(a) * nr + fs * 0.04);
        }
        c.shadowBlur = 0;
        st.cache = off;
    }

    /* ── a dart, tip at (x, y) (css px), pointing into the board ── */
    function drawDart(x, y, color, scale, tilt, alpha) {
        const L = st.R * 0.30 * scale;
        ctx.save();
        ctx.globalAlpha = alpha == null ? 1 : alpha;
        ctx.translate(x, y);
        ctx.rotate(tilt);
        // the shadow it casts on the board (light from above-left)
        ctx.strokeStyle = 'rgba(0,0,0,.26)';
        ctx.lineCap = 'round';
        ctx.lineWidth = L * 0.055;
        ctx.beginPath(); ctx.moveTo(L * 0.05, L * 0.06); ctx.lineTo(L * 0.48, -L * 0.62); ctx.stroke();
        // point
        ctx.strokeStyle = '#d7dde6'; ctx.lineWidth = Math.max(1, L * 0.025);
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(L * 0.04, -L * 0.16); ctx.stroke();
        // barrel: metal with a knurled grip
        const bx0 = L * 0.04, by0 = -L * 0.16, bx1 = L * 0.14, by1 = -L * 0.5;
        const bg = ctx.createLinearGradient(bx0 - L * 0.04, by0, bx0 + L * 0.06, by0);
        bg.addColorStop(0, '#5d6573'); bg.addColorStop(0.45, '#eef2f7'); bg.addColorStop(1, '#4a515d');
        ctx.strokeStyle = bg; ctx.lineWidth = L * 0.085;
        ctx.beginPath(); ctx.moveTo(bx0, by0); ctx.lineTo(bx1, by1); ctx.stroke();
        ctx.strokeStyle = 'rgba(0,0,0,.28)'; ctx.lineWidth = Math.max(0.6, L * 0.012);
        for (let t = 0.25; t < 0.85; t += 0.12) {
            const px = bx0 + (bx1 - bx0) * t, py = by0 + (by1 - by0) * t;
            ctx.beginPath(); ctx.moveTo(px - L * 0.035, py - L * 0.01); ctx.lineTo(px + L * 0.035, py + L * 0.01); ctx.stroke();
        }
        // shaft
        ctx.strokeStyle = '#1c2230'; ctx.lineWidth = L * 0.045;
        ctx.beginPath(); ctx.moveTo(bx1, by1); ctx.lineTo(L * 0.21, -L * 0.74); ctx.stroke();
        // flight: two vanes in the player's colour, seen at an angle
        const fx = L * 0.21, fy = -L * 0.74;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(fx, fy);
        ctx.lineTo(fx + L * 0.2, fy - L * 0.12);
        ctx.lineTo(fx + L * 0.16, fy - L * 0.32);
        ctx.lineTo(fx + L * 0.05, fy - L * 0.26);
        ctx.closePath(); ctx.fill();
        ctx.globalAlpha *= 0.82;
        ctx.beginPath();
        ctx.moveTo(fx, fy);
        ctx.lineTo(fx - L * 0.15, fy - L * 0.16);
        ctx.lineTo(fx - L * 0.07, fy - L * 0.33);
        ctx.lineTo(fx + L * 0.05, fy - L * 0.26);
        ctx.closePath(); ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,.65)'; ctx.lineWidth = Math.max(0.7, L * 0.012);
        ctx.beginPath(); ctx.moveTo(fx, fy); ctx.lineTo(fx + L * 0.05, fy - L * 0.26); ctx.stroke();
        ctx.restore();
    }

    const px = (n) => st.cx + n * st.R;
    const py = (n) => st.cy + n * st.R;

    /* ── the frame ── */
    function draw() {
        if (!st.size) return;
        if (!st.cache) buildCache();
        const k = st.dpr;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.drawImage(st.cache, 0, 0);
        ctx.setTransform(k, 0, 0, k, 0, 0);
        let sx = 0, sy = 0;
        if (st.shake > 0 && !reduced) {
            const a = st.shake * st.shake * st.R * 0.012;
            sx = (Math.random() - 0.5) * a; sy = (Math.random() - 0.5) * a;
        }
        ctx.save();
        ctx.translate(sx, sy);
        if (st.highlight) for (const h of st.highlight) drawHighlight(h);
        if (st.hover) drawHighlight(st.hover, true);
        if (st.heat) drawHeat(st.heat);
        const now = performance.now();
        for (const d of st.darts) {
            const age = now - (d.born || 0);
            const wob = !reduced && age < 380 ? Math.sin((age / 380) * Math.PI * 5) * 0.09 * (1 - age / 380) : 0;
            drawDart(px(d.x), py(d.y), d.color, 1, d.tilt + wob, d.ghost ? 0.35 : 1);
        }
        for (const f of st.fx) {
            const t = f.t;
            ctx.strokeStyle = f.color; ctx.globalAlpha = Math.max(0, 1 - t) * 0.8;
            ctx.lineWidth = 2;
            ctx.beginPath(); ctx.arc(px(f.x), py(f.y), st.R * (0.02 + t * 0.09), 0, Math.PI * 2); ctx.stroke();
            ctx.globalAlpha = 1;
        }
        if (st.flight) {
            const f = st.flight;
            const e = 1 - Math.pow(1 - f.t, 2.4);
            const x = f.sx + (f.ex - f.sx) * e;
            const y = f.sy + (f.ey - f.sy) * e - Math.sin(Math.PI * e) * 0.18;
            const sc = 2.6 - 1.6 * e;
            drawDart(px(x), py(y), f.color, sc, f.tilt * e, 0.25 + 0.75 * Math.min(1, f.t * 3));
        }
        for (const p of st.pops) {
            ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
            ctx.font = `800 ${Math.max(13, Math.round(st.R * 0.085))}px system-ui, sans-serif`;
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(0,0,0,.65)';
            const tx = px(p.x), ty = py(p.y) - (1 - p.life) * st.R * 0.12;
            ctx.strokeText(p.text, tx, ty);
            ctx.fillStyle = p.color; ctx.fillText(p.text, tx, ty);
            ctx.globalAlpha = 1;
        }
        ctx.restore();
        if (st.sweep) drawSweep(st.sweep);
        if (st.aim) drawAim(st.aim);
        if (st.loupe) drawLoupe(st.loupe);
    }

    function bedPath(h) {
        // the outline of a bed (sector × ring) or the bull, for highlights
        const R = st.R, cx = st.cx, cy = st.cy;
        ctx.beginPath();
        if (h.s === 25) {
            ctx.arc(cx, cy, (h.m === 2 ? RING.bullInner : RING.bullOuter) * R, 0, Math.PI * 2);
            return;
        }
        const a = sectorAngle(h.s);
        let r0 = RING.bullOuter, r1 = 1;
        if (h.m === 3) { r0 = RING.tripleInner; r1 = RING.tripleOuter; }
        else if (h.m === 2) { r0 = RING.doubleInner; r1 = 1; }
        else if (h.m === 1 && h.outer) { r0 = RING.tripleOuter; r1 = RING.doubleInner; }
        ctx.arc(cx, cy, r1 * R, a - SEG_ARC / 2, a + SEG_ARC / 2);
        ctx.arc(cx, cy, r0 * R, a + SEG_ARC / 2, a - SEG_ARC / 2, true);
        ctx.closePath();
    }

    function drawHighlight(h, soft) {
        ctx.save();
        bedPath(h);
        ctx.fillStyle = soft ? 'rgba(255,255,255,.22)' : 'rgba(250,204,21,.20)';
        ctx.fill();
        ctx.lineWidth = soft ? 2 : 2.5;
        ctx.strokeStyle = soft ? 'rgba(255,255,255,.85)' : 'rgba(250,204,21,.95)';
        ctx.shadowColor = soft ? 'transparent' : 'rgba(250,204,21,.8)';
        ctx.shadowBlur = soft ? 0 : 10;
        ctx.stroke();
        ctx.restore();
    }

    function drawHeat(heat) {
        // practice: where the darts went, the newest brightest; the group's centre and spread
        const n = heat.points.length;
        heat.points.forEach((p, i) => {
            const a = 0.25 + 0.75 * ((i + 1) / n);
            ctx.fillStyle = `rgba(56,189,248,${a})`;
            ctx.beginPath(); ctx.arc(px(p.x), py(p.y), Math.max(2.5, st.R * 0.012), 0, Math.PI * 2); ctx.fill();
        });
        if (heat.group) {
            const g = heat.group;
            ctx.save();
            ctx.strokeStyle = 'rgba(56,189,248,.95)'; ctx.setLineDash([5, 4]); ctx.lineWidth = 2;
            ctx.beginPath(); ctx.arc(px(g.x), py(g.y), Math.max(4, g.r * st.R), 0, Math.PI * 2); ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = '#38bdf8';
            ctx.beginPath(); ctx.arc(px(g.x), py(g.y), 3, 0, Math.PI * 2); ctx.fill();
            ctx.restore();
        }
    }

    function drawSweep(sw) {
        ctx.save();
        const R = st.R, top = st.cy - R * 1.12, bot = st.cy + R * 1.12, l = st.cx - R * 1.12, r = st.cx + R * 1.12;
        ctx.lineWidth = 2.5;
        ctx.shadowColor = 'rgba(0,0,0,.7)'; ctx.shadowBlur = 4;
        if (sw.x != null) {
            ctx.strokeStyle = sw.phase === 'x' ? '#facc15' : 'rgba(250,204,21,.55)';
            ctx.beginPath(); ctx.moveTo(px(sw.x), top); ctx.lineTo(px(sw.x), bot); ctx.stroke();
        }
        if (sw.y != null) {
            ctx.strokeStyle = '#facc15';
            ctx.beginPath(); ctx.moveTo(l, py(sw.y)); ctx.lineTo(r, py(sw.y)); ctx.stroke();
        }
        ctx.restore();
    }

    function drawAim(a) {
        const x = px(a.x), y = py(a.y);
        const calm = a.calm == null ? 0.5 : a.calm; // 0 shaking … 1 steady
        const rr = Math.max(9, st.R * (0.035 + (1 - calm) * 0.05));
        ctx.save();
        ctx.lineWidth = 3.2; ctx.strokeStyle = 'rgba(0,0,0,.55)';
        ctx.beginPath(); ctx.arc(x, y, rr, 0, Math.PI * 2); ctx.stroke();
        ctx.lineWidth = 1.8;
        ctx.strokeStyle = calm > 0.72 ? '#4ade80' : (calm > 0.4 ? '#fde047' : '#fb923c');
        ctx.beginPath(); ctx.arc(x, y, rr, 0, Math.PI * 2); ctx.stroke();
        // the steadiness arc fills as the hand calms
        if (a.holding) {
            ctx.lineWidth = 3;
            ctx.beginPath(); ctx.arc(x, y, rr + 5, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * calm); ctx.stroke();
        }
        const t = rr * 0.55, g = rr * 1.5;
        ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(0,0,0,.55)';
        ctx.beginPath();
        ctx.moveTo(x - g, y); ctx.lineTo(x - t, y); ctx.moveTo(x + t, y); ctx.lineTo(x + g, y);
        ctx.moveTo(x, y - g); ctx.lineTo(x, y - t); ctx.moveTo(x, y + t); ctx.lineTo(x, y + g);
        ctx.stroke();
        ctx.lineWidth = 1.4; ctx.strokeStyle = '#ffffff';
        ctx.stroke();
        ctx.fillStyle = '#fff';
        ctx.beginPath(); ctx.arc(x, y, 1.6, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
    }

    function drawLoupe(lp) {
        // the magnifier above a finger: the board under it, ×2.2, and the aim's centre
        const k = st.dpr;
        const rad = Math.min(st.size * 0.17, 74);
        let lx = px(lp.x), ly = py(lp.y) - rad - 46;
        if (ly - rad < 4) { ly = py(lp.y) + rad + 46; }
        lx = Math.max(rad + 4, Math.min(st.size - rad - 4, lx));
        const zoom = 2.2;
        const fx = px(lp.x), fy = py(lp.y);
        ctx.save();
        ctx.beginPath(); ctx.arc(lx, ly, rad, 0, Math.PI * 2); ctx.closePath();
        ctx.shadowColor = 'rgba(0,0,0,.6)'; ctx.shadowBlur = 14; ctx.fillStyle = '#000'; ctx.fill();
        ctx.shadowBlur = 0;
        ctx.clip();
        const sw = (rad * 2) / zoom;
        ctx.drawImage(st.cache, (fx - sw / 2) * k, (fy - sw / 2) * k, sw * k, sw * k, lx - rad, ly - rad, rad * 2, rad * 2);
        // the darts already in the board, magnified too
        ctx.translate(lx - fx * zoom, ly - fy * zoom);
        ctx.scale(zoom, zoom);
        for (const d of st.darts) drawDart(px(d.x), py(d.y), d.color, 1, d.tilt, 0.9);
        ctx.restore();
        ctx.save();
        ctx.lineWidth = 2.5; ctx.strokeStyle = 'rgba(255,255,255,.9)';
        ctx.beginPath(); ctx.arc(lx, ly, rad, 0, Math.PI * 2); ctx.stroke();
        ctx.strokeStyle = lp.color || '#fde047'; ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(lx - 12, ly); ctx.lineTo(lx - 4, ly); ctx.moveTo(lx + 4, ly); ctx.lineTo(lx + 12, ly);
        ctx.moveTo(lx, ly - 12); ctx.lineTo(lx, ly - 4); ctx.moveTo(lx, ly + 4); ctx.lineTo(lx, ly + 12);
        ctx.stroke();
        if (lp.label) {
            ctx.font = '800 14px system-ui, sans-serif';
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            const w = ctx.measureText(lp.label).width + 14;
            ctx.fillStyle = 'rgba(0,0,0,.72)';
            ctx.beginPath(); ctx.roundRect ? ctx.roundRect(lx - w / 2, ly + rad - 26, w, 20, 6) : ctx.rect(lx - w / 2, ly + rad - 26, w, 20); ctx.fill();
            ctx.fillStyle = '#fff'; ctx.fillText(lp.label, lx, ly + rad - 16);
        }
        ctx.restore();
    }

    /* ── the loop: runs while something moves ── */
    function busy() {
        return !!(st.flight || st.fx.length || st.pops.length || st.shake > 0 || st.aim || st.sweep || st.loupe
            || st.darts.some((d) => performance.now() - (d.born || 0) < 400) || (st.onFrame && st.onFrame.active && st.onFrame.active()));
    }
    function frame(ts) {
        st.raf = 0;
        const dt = st.last ? Math.min(250, ts - st.last) : 16;
        st.last = ts;
        if (st.onFrame) st.onFrame(dt);
        if (st.flight) {
            // by the wall clock: a browser that hands out few frames (low power, a busy page)
            // must not stretch a 0.3 s flight into seconds and swallow the next press
            st.flight.t = Math.min(1, (performance.now() - st.flight.start) / st.flight.dur);
            if (st.flight.t >= 1) {
                const f = st.flight; st.flight = null;
                land(f);
            }
        }
        st.fx = st.fx.filter((f) => (f.t += dt / 420) < 1);
        st.pops = st.pops.filter((p) => (p.life -= dt / 900) > 0);
        if (st.shake > 0) st.shake = Math.max(0, st.shake - dt / 260);
        draw();
        if (busy()) kick(); else st.last = 0;
    }
    function kick() { if (!st.raf) st.raf = requestAnimationFrame(frame); }

    function land(f) {
        const d = { x: f.ex, y: f.ey, color: f.color, tilt: f.tilt, born: performance.now() };
        st.darts.push(d);
        if (!reduced) {
            st.shake = f.hot ? 0.9 : 0.5;
            st.fx.push({ x: f.ex, y: f.ey, t: 0, color: f.hot ? '#facc15' : 'rgba(255,255,255,.85)' });
        }
        if (f.label) st.pops.push({ x: f.ex, y: f.ey - 0.07, text: f.label, color: f.hot ? '#fde047' : (f.miss ? '#fca5a5' : '#ffffff'), life: 1 });
        if (f.done) f.done();
        kick();
    }

    return {
        el: canvas,
        resize, setTheme, draw: () => { draw(); },
        get theme() { return st.theme; },
        get size() { return st.size; },
        kick,
        /** css px inside the canvas → normalised board coordinates */
        toBoard(clientX, clientY) {
            const r = canvas.getBoundingClientRect();
            return { x: (clientX - r.left - st.cx) / st.R, y: (clientY - r.top - st.cy) / st.R };
        },
        pxPerUnit() { return st.R; },
        /** a dart flies from below the viewer to (x, y) and lands; done() after it sticks */
        throwDart({ x, y, color, label, hot, miss, done, fromX }) {
            const f = { sx: fromX != null ? fromX : (Math.random() - 0.5) * 0.5, sy: 1.25, ex: x, ey: y,
                t: 0, start: performance.now(), dur: reduced ? 1 : 300, tilt: (Math.random() - 0.5) * 0.3, color, label, hot, miss, done };
            st.flight = f;
            kick();
        },
        /** a dart placed without flight (replaying a saved match, the real board) */
        placeDart({ x, y, color, ghost }) {
            st.darts.push({ x, y, color, tilt: (Math.random() - 0.5) * 0.3, born: 0, ghost: !!ghost });
            draw();
        },
        pop(x, y, text, color) { st.pops.push({ x, y, text, color: color || '#fff', life: 1 }); kick(); },
        clearDarts() { st.darts = []; draw(); },
        dartsCount() { return st.darts.length; },
        popDart() { st.darts.pop(); draw(); },
        setAim(a) { st.aim = a; if (a) kick(); else draw(); },
        setSweep(sw) { st.sweep = sw; if (sw) kick(); else draw(); },
        setLoupe(lp) { st.loupe = lp; if (lp) kick(); else draw(); },
        setHighlight(h) { st.highlight = h ? (Array.isArray(h) ? h : [h]) : null; draw(); },
        setHover(h) { st.hover = h; draw(); },
        setHeat(h) { st.heat = h; draw(); },
        onFrame(fn) { st.onFrame = fn; },
        busy,
    };
}
